import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY_PLAN = { name: "", audience: "school", price_gbp: 9.99, max_pupils: 100, interval: "year", mode: "subscription", duration_years: 1, active: true };

export default function SuperPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PLAN);

  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_PLAN); setEditing("new"); };
  const openEdit = (p) => { setForm({ name: p.name, audience: p.audience, price_gbp: p.price_gbp, max_pupils: p.max_pupils, interval: p.interval || "year", mode: p.mode || "subscription", duration_years: p.duration_years || 1, active: p.active }); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/plans", form);
      else await api.put(`/admin/plans/${editing}`, form);
      toast.success("Plan saved — Stripe price syncs automatically at checkout");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Plans in use are archived instead.`)) return;
    try {
      const { data } = await api.delete(`/admin/plans/${p.id}`);
      toast.success(data.archived ? "Plan in use — archived instead" : "Plan deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="super-plans-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="nb-label">Plans</span>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Subscription plans</h1>
          <p className="text-zinc-500 mt-2 max-w-xl">School plans cap pupil numbers; the parent plan unlocks the parent timetable builder. Yearly billing via Stripe.</p>
        </div>
        <button onClick={openNew} data-testid="plan-new-btn" className="nb-btn bg-[#A7E4D3] px-5 py-2.5"><Plus className="w-4 h-4" /> New plan</button>
      </div>

      <div className="mt-8 nb-card overflow-hidden">
        <table className="w-full border-collapse" data-testid="plans-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Name</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Audience</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Price</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Pupil cap</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Status</th>
              <th className="p-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t-2 border-zinc-900" data-testid={`plan-row-${p.id}`}>
                <td className="p-3 font-bold">{p.name}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${p.audience === "school" ? "bg-[#7DD3FC]" : "bg-[#F9A8D4]"}`}>{p.audience}</span>
                </td>
                <td className="p-3 font-bold">£{Number(p.price_gbp).toFixed(2)}{p.mode === "one_time" ? ` one-off·${p.duration_years || 1}y` : `/${p.interval === "year" ? "yr" : "mo"}`}</td>
                <td className="p-3">{p.audience === "parent" ? "—" : p.max_pupils}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${p.active ? "bg-[#A7E4D3]" : "bg-zinc-200 text-zinc-500"}`}>{p.active ? "active" : "archived"}</span>
                </td>
                <td className="p-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`} className="p-2 rounded-lg border-2 border-zinc-900 bg-[#FDE68A] hover:brightness-105 transition-colors duration-150">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`plan-delete-${p.id}`} className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                      <Trash2 className="w-4 h-4 text-red-700" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-sm border-t-2 border-zinc-900" data-testid="plans-empty">No plans yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="plan-edit-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl">{editing === "new" ? "New plan" : "Edit plan"}</h2>
              <button onClick={() => setEditing(null)} data-testid="plan-edit-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="nb-label" htmlFor="plan-name">Name</label>
                <input id="plan-name" className="nb-input mt-2" data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="nb-label" htmlFor="plan-audience">Audience</label>
                  <select id="plan-audience" className="nb-input mt-2" data-testid="plan-audience-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                    <option value="school">School</option>
                    <option value="parent">Parent</option>
                  </select>
                </div>
                <div>
                  <label className="nb-label" htmlFor="plan-interval">Billing</label>
                  <select id="plan-interval" className="nb-input mt-2" data-testid="plan-interval-select" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}>
                    <option value="year">Yearly</option>
                    <option value="month">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="nb-label" htmlFor="plan-mode">Payment type</label>
                  <select id="plan-mode" className="nb-input mt-2" data-testid="plan-mode-select" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                    <option value="subscription">Subscription</option>
                    <option value="one_time">One-off payment</option>
                  </select>
                </div>
                {form.mode === "one_time" && (
                  <div>
                    <label className="nb-label" htmlFor="plan-duration">Years covered</label>
                    <input id="plan-duration" type="number" min="1" max="10" className="nb-input mt-2" data-testid="plan-duration-input"
                      value={form.duration_years} onChange={(e) => setForm({ ...form, duration_years: parseInt(e.target.value) || 1 })} />
                  </div>
                )}
                <div>
                  <label className="nb-label" htmlFor="plan-price">Price (£)</label>
                  <input id="plan-price" type="number" step="0.01" min="0" className="nb-input mt-2" data-testid="plan-price-input" value={form.price_gbp} onChange={(e) => setForm({ ...form, price_gbp: parseFloat(e.target.value) || 0 })} required />
                </div>
                <div>
                  <label className="nb-label" htmlFor="plan-cap">Pupil cap</label>
                  <input id="plan-cap" type="number" min="1" className="nb-input mt-2" data-testid="plan-cap-input" value={form.max_pupils} onChange={(e) => setForm({ ...form, max_pupils: parseInt(e.target.value) || 1 })} required />
                </div>
              </div>
              <label className="flex items-center gap-2 font-bold text-sm">
                <input type="checkbox" data-testid="plan-active-checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-zinc-900" />
                Active (available for purchase)
              </label>
              <button type="submit" data-testid="plan-save-btn" className="nb-btn w-full bg-[#1E3A8A] text-white py-2.5"><Check className="w-4 h-4" /> Save plan</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
